import { useEffect, useRef, useState } from "react";
import log from "@/lib/log";

// Connect to the FastAPI WebSocket /api/ws/events and stream events into state.
// Falls back to a polling-style timer if connection fails.
export default function useLiveEvents(seed = [], { max = 30 } = {}) {
  const [events, setEvents] = useState(seed);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const retryRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const wsUrl = base.replace(/^http/, "ws") + "/api/ws/events";

    const connect = () => {
      if (cancelled) return;
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        ws.onopen = () => {
          retryRef.current = 0;
          setConnected(true);
        };
        ws.onmessage = (e) => {
          try {
            const msg = JSON.parse(e.data);
            if (msg.type === "hello") return;
            setEvents((prev) => [msg, ...prev].slice(0, max));
          } catch (err) {
            log.warn("[ws] bad frame:", err?.message || err);
          }
        };
        ws.onclose = () => {
          setConnected(false);
          const delay = Math.min(10000, 1000 * Math.pow(2, retryRef.current));
          retryRef.current += 1;
          if (!cancelled) setTimeout(connect, delay);
        };
        ws.onerror = () => {
          try { ws.close(); } catch (e) { /* socket already closing */ }
        };
      } catch (e) {
        log.warn("[ws] connect failed:", e?.message || e);
        setTimeout(connect, 3000);
      }
    };

    connect();
    return () => {
      cancelled = true;
      try { wsRef.current?.close(); } catch (e) { /* noop */ }
    };
  }, [max]);

  return { events, connected };
}
